import { apiFetch } from "@common/lib/api";

/** EN-1250: AVImark exam setup (calibration). Contract: specs/2026-10-07-en-1250-avimark-calibration-spec.md, B5. */

export type CalibrationStatus = "checking" | "needs_visit" | "failed" | "timed_out" | "done";

export interface AvimarkCalibrationState {
    effectiveStatus: CalibrationStatus;
    setupReady: boolean;
    run: {
        id: string;
        status: CalibrationStatus;
        reasonCode: string | null;
        step: string | null;
        startedAt: string;
        finishedAt: string | null;
        deadlineAt: string;
    } | null;
    copiedFrom: { patientName: string | null; visitDate: string | null; doctorName: string | null } | null;
    bodySystemCount: number;
    /** The sample exam changed and the replace run ended without done (failed, needs_visit or timed_out), so
     *  exports are refused even though setupReady is true. run.reasonCode says why. */
    sampleChangeUnresolved?: boolean;
}

export interface AvimarkRecentExam {
    anchorRecno: number;
    medicalRecno: number;
    patientRecno: number;
    patientName: string | null;
    species: string | null;
    date: string;
    doctorCode: string;
    doctorName: string | null;
    doctorCopyable: boolean;
    exportedByHappyDoc: boolean;
}

export interface AvimarkExamRow {
    code: string;
    name: string;
    ticked: boolean;
    text: string;
    kind: "abnormal" | "normal" | "not-examined";
}

export class AvimarkBridgeUnreachableError extends Error {
    constructor(message?: string) {
        super(message);
        // ES5 transpile breaks instanceof for Error subclasses without this. Same in the two classes below.
        Object.setPrototypeOf(this, new.target.prototype);
    }
}
export class AvimarkSetupAlreadyRunningError extends Error {
    constructor(message?: string) {
        super(message);
        // Keeps instanceof working; see the note on AvimarkBridgeUnreachableError.
        Object.setPrototypeOf(this, new.target.prototype);
    }
}
export class AvimarkSampleInvalidError extends Error {
    constructor(message?: string) {
        super(message);
        // Keeps instanceof working; see the note on AvimarkBridgeUnreachableError.
        Object.setPrototypeOf(this, new.target.prototype);
    }
}

function statusOf(e: unknown): number | undefined {
    return (e as { response?: { status?: number } } | null)?.response?.status;
}

async function call<T>(path: string, init: { method: "GET" | "POST"; json?: unknown }): Promise<T> {
    try {
        // The backup page shows its own messages, so no toast; 409/422/503 are expected outcomes.
        // The server answers 503 only after its ~61 s bridge wait, past ky's 30 s default, so wait longer.
        const data = await apiFetch<T>(path, { ...init, timeout: 75_000, silent: true, skipSentryOnStatuses: [409, 422, 503] });
        if (data === undefined || data === null) throw new Error(`Empty response from ${path}`);
        return data;
    } catch (e) {
        const s = statusOf(e);
        if (s === 503 || (e as { name?: string } | null)?.name === "TimeoutError") throw new AvimarkBridgeUnreachableError("HappyBridge can't reach AVImark right now");
        if (s === 409) throw new AvimarkSetupAlreadyRunningError("AVImark exam setup is already running");
        if (s === 422) throw new AvimarkSampleInvalidError("That exam can't be used");
        throw e;
    }
}

export const fetchAvimarkCalibration = () =>
    call<AvimarkCalibrationState>("integration/avimark/calibration", { method: "GET" });
export const startAvimarkCalibration = () =>
    call<{ runId: string }>("integration/avimark/calibration/run", { method: "POST", json: {} });
export const fetchAvimarkRecentExams = (before?: number) =>
    call<{ exams: AvimarkRecentExam[]; nextBefore: number | null }>(
        `integration/avimark/calibration/recent-exams${before ? `?before=${before}` : ""}`,
        { method: "GET" },
    );
export const fetchAvimarkExam = (medicalRecno: number) =>
    call<{ rows: AvimarkExamRow[] }>(`integration/avimark/calibration/exams/${medicalRecno}`, { method: "GET" });
export const chooseAvimarkExam = (sample: { patientRecno: number; anchorRecno: number; medicalRecno: number }) =>
    call<{ runId: string }>("integration/avimark/calibration/use-exam", { method: "POST", json: sample });
